import React, { useState, useEffect, useRef } from 'react';
import syncService from '../../services/syncService';
import Spinner from './Spinner';
import './CopyConversionsModal.css';

// The "choose another sync project" list, shared by every copy-from-project wizard.
//
// EXTRACTED RATHER THAN COPIED. Conversions, default values and task settings all begin the same way —
// list the other projects in this chain, optionally narrowed to the same template, search them, group
// them by hotel, pick one, show a badge saying what it holds. Only what happens NEXT differs. Two
// copies of this would drift on the details that matter least and are noticed last: which projects are
// excluded, how the search matches, whether the selection survives a re-render.
//
// The parent owns `selected`, the loading state and the badge, because those depend on what is being
// copied: a conversions wizard counts rows, a settings wizard counts settings. `renderBadge` is how the
// parent says what to show without this component knowing anything about either.
const SyncProjectPicker = ({
    accountId,
    currentSyncUuid,
    templateName,             // when set, only projects on the same template are offered
    selected,                 // the currently selected project (owned by the parent)
    busy = false,             // parent is loading the selection — rows go non-interactive
    onSelect,
    renderBadge,              // (project) => node, drawn on the selected row
    emptyLabel = 'sync projects',
}) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const listRef                 = useRef(null);

    useEffect(() => {
        if (selected && listRef.current) {
            const el = listRef.current.querySelector('.ccm-project-row--selected');
            if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selected]);

    useEffect(() => {
        if (!accountId) { setLoading(false); return; }
        syncService.getSyncProjects(accountId, null, null)
            .then(data => {
                const all = Array.isArray(data) ? data : [];
                setProjects(all.filter(p =>
                    p.syncuuid !== currentSyncUuid &&
                    (!templateName || p.templateName === templateName)
                ));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [accountId, currentSyncUuid, templateName]);

    const lc = search.toLowerCase();
    const filtered = lc
        ? projects.filter(p =>
            (p.projectName || '').toLowerCase().includes(lc) ||
            (p.hotelName   || '').toLowerCase().includes(lc))
        : projects;

    const byHotel = filtered.reduce((acc, p) => {
        const key = p.hotelName || `Hotel ${p.hotelId}`;
        (acc[key] = acc[key] || []).push(p);
        return acc;
    }, {});

    return (
        <>
            {!loading && projects.length > 0 && (
                <input
                    className="ccm-search"
                    type="text"
                    placeholder="Search by project or hotel name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoFocus
                />
            )}

            {loading && <div className="ccm-loading"><Spinner size="sm" /> Loading projects...</div>}

            {!loading && projects.length === 0 && (
                <div className="ccm-empty">
                    No other {emptyLabel}{templateName ? ` using the "${templateName}" template` : ''} were found in this chain.
                </div>
            )}

            {!loading && projects.length > 0 && filtered.length === 0 && (
                <div className="ccm-empty">No projects match "{search}".</div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="ccm-list" ref={listRef}>
                    {Object.entries(byHotel).map(([hotelName, hotelProjects]) => (
                        <div key={hotelName} className="ccm-hotel-group">
                            <div className="ccm-hotel-label">{hotelName}</div>
                            {hotelProjects.map(p => {
                                const isSelected = selected?.syncuuid === p.syncuuid;
                                return (
                                    <div
                                        key={p.syncuuid}
                                        className={`ccm-project-row${isSelected ? ' ccm-project-row--selected' : ''}${busy ? ' ccm-project-row--wait' : ''}`}
                                        onClick={() => !busy && onSelect(p)}
                                    >
                                        <div>
                                            <div className="ccm-project-name">{p.projectName}</div>
                                            {p.templateName && <div className="ccm-project-template">{p.templateName}</div>}
                                        </div>
                                        <div className="ccm-project-meta">
                                            {isSelected && renderBadge && renderBadge(p)}
                                            {isSelected && <span className="ccm-selected-check">&#10003;</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default SyncProjectPicker;
